import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Tv, Clock, CheckCircle2 } from "lucide-react";

export default function Matches({ sport = "football", embedded = false }) {
  const [data, setData] = useState({ live: [], upcoming: [], finished: [] });
  const [wallet, setWallet] = useState({ balance_usdt: 0 });
  const [myBets, setMyBets] = useState([]);
  const [slip, setSlip] = useState(null); // {match, pick}
  const [amount, setAmount] = useState("5");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/matches?sport=${sport}`).then(({ data }) => setData(data)).catch(() => {});
  };
  const loadBets = () => api.get("/matches/bets/me").then(({ data }) => setMyBets(data)).catch(() => {});

  useEffect(() => {
    load();
    loadBets();
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, []);

  const placeBet = async () => {
    if (!slip) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/matches/${slip.match.match_id}/bet`, {
        pick: slip.pick,
        amount: parseFloat(amount),
      });
      setWallet((w) => ({ ...w, balance_usdt: res.balance_usdt }));
      toast.success(`Bet placed: ${pickLabel(slip)} @ ${res.odds}×`);
      setSlip(null);
      loadBets();
    } catch (err) {
      toast.error(apiError(err, "Bet failed"));
    } finally {
      setBusy(false);
    }
  };

  const pickLabel = (s) =>
    s.pick === "home" ? s.match.home : s.pick === "away" ? s.match.away : "Draw";

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8" data-testid="matches-page">
      {!embedded && (
        <>
          <div className="label-mono mb-2 text-brand-cyan">// live_matches</div>
          <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-1">MATCH CENTER.</h1>
          <p className="text-white/50 font-body mb-8">Live scores from the metaverse leagues. Pick a result, back it with USDT. <span className="text-brand-amber">DEMO feed — real leagues coming soon.</span></p>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-6">
          <section>
            <div className="label-mono mb-3 flex items-center gap-2 text-neon-red"><Tv className="w-3.5 h-3.5" /> LIVE NOW</div>
            <div className="space-y-3" data-testid="live-matches-list">
              {data.live.length === 0 && <div className="panel p-5 text-white/40 text-sm font-mono">No live matches — kickoff soon…</div>}
              {data.live.map((m) => (
                <MatchCard key={m.match_id} m={m} live sport={sport} onPick={(pick) => setSlip({ match: m, pick })} slip={slip} />
              ))}
            </div>
          </section>

          <section>
            <div className="label-mono mb-3 flex items-center gap-2 text-brand-cyan"><Clock className="w-3.5 h-3.5" /> UPCOMING</div>
            <div className="space-y-3" data-testid="upcoming-matches-list">
              {data.upcoming.map((m) => (
                <MatchCard key={m.match_id} m={m} sport={sport} onPick={(pick) => setSlip({ match: m, pick })} slip={slip} />
              ))}
            </div>
          </section>

          <section>
            <div className="label-mono mb-3 flex items-center gap-2 text-white/40"><CheckCircle2 className="w-3.5 h-3.5" /> FULL TIME</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="finished-matches-list">
              {data.finished.map((m) => (
                <div key={m.match_id} className="panel p-4 flex items-center justify-between opacity-70">
                  <div className="text-sm font-body">{m.home} <span className="font-mono text-white">{m.score_home}-{m.score_away}</span> {m.away}</div>
                  <span className="label-mono text-white/40">FT</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="panel p-5" data-testid="matches-wallet-mini">
            <div className="label-mono">Wallet</div>
            <div className="font-mono text-2xl font-bold mt-1 text-neon-green glow-green">{wallet.balance_usdt?.toFixed(2)} <span className="text-xs text-white/50">USDT</span></div>
          </div>

          <div className="panel p-5 space-y-4" data-testid="bet-slip-panel">
            <div className="label-mono">Bet Slip</div>
            {!slip ? (
              <p className="text-white/40 text-xs font-mono">Tap odds on a match to build your slip.</p>
            ) : (
              <>
                <div className="border border-brand-cyan/40 bg-black/40 p-3">
                  <div className="text-xs font-mono text-white/50">{slip.match.home} vs {slip.match.away}</div>
                  <div className="font-display font-bold mt-1">{pickLabel(slip)} <span className="text-brand-cyan font-mono">@ {slip.match.odds[slip.pick]}×</span></div>
                </div>
                <div>
                  <label className="label-mono">Amount (USDT) · min 5</label>
                  <input type="number" min="5" step="1" className="field mt-2" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="bet-amount-input" />
                </div>
                <div className="font-mono text-xs flex justify-between text-white/50">
                  <span>Potential return</span>
                  <span className="text-neon-green">{((parseFloat(amount) || 0) * slip.match.odds[slip.pick]).toFixed(2)} USDT</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={placeBet} disabled={busy || (parseFloat(amount) || 0) < 5} className="btn-neon flex-1" data-testid="place-match-bet-button">{busy ? "…" : "PLACE BET"}</button>
                  <button onClick={() => setSlip(null)} className="btn-ghost" data-testid="clear-slip-button">✕</button>
                </div>
              </>
            )}
          </div>

          <div className="panel p-5" data-testid="my-bets-panel">
            <div className="label-mono mb-3">My Bets</div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {myBets.length === 0 && <p className="text-white/40 text-xs font-mono">No bets yet.</p>}
              {myBets.map((b) => (
                <div key={b.bet_id} className="border border-neon-line bg-black/30 p-2.5 text-xs font-mono flex items-center justify-between">
                  <div>
                    <div className="text-white/70">{b.home} v {b.away}</div>
                    <div className="text-white/40">{b.pick === "home" ? b.home : b.pick === "away" ? b.away : "Draw"} @ {b.odds}× · {b.amount} USDT</div>
                  </div>
                  <span className={b.status === "won" ? "text-neon-green" : b.status === "lost" ? "text-neon-red" : "text-brand-cyan"}>
                    {b.status === "won" ? `+${b.payout.toFixed(2)}` : b.status.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function MatchCard({ m, live, onPick, slip, sport }) {
  const boxing = sport === "boxing";
  const cutoff = boxing ? 10 : 80;
  const closed = live && m.minute >= cutoff;
  return (
    <div className="panel p-4" data-testid={`match-card-${m.match_id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-mono text-white/40">{m.league}</span>
        {live ? (
          <span className="label-mono text-neon-red flex items-center gap-1.5"><span className="dot-live" /> {boxing ? `R${m.minute}` : `${m.minute}'`}</span>
        ) : (
          <span className="label-mono text-brand-cyan">kickoff in {Math.max(0, m.kickoff_in ?? 0)}s</span>
        )}
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="font-display font-bold text-lg flex-1">{m.home}</div>
        <div className="font-mono text-2xl font-black text-white px-3">{live ? `${m.score_home} - ${m.score_away}` : "vs"}</div>
        <div className="font-display font-bold text-lg flex-1 text-right">{m.away}</div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        {["home", "draw", "away"].map((pick) => {
          const selected = slip?.match.match_id === m.match_id && slip?.pick === pick;
          return (
            <button
              key={pick}
              disabled={closed}
              onClick={() => onPick(pick)}
              className={`py-2 border font-mono text-xs transition-colors ${selected ? "border-brand-cyan bg-brand-cyan/15 text-brand-cyan" : "border-neon-line text-white/60 hover:border-brand-cyan hover:text-white"} disabled:opacity-30`}
              data-testid={`odds-${m.match_id}-${pick}`}
            >
              {pick === "home" ? "1" : pick === "draw" ? "X" : "2"} · {m.odds[pick]}×
            </button>
          );
        })}
      </div>
      {closed && <div className="label-mono text-white/30 mt-2">betting closed ({boxing ? "R10+" : "80'+"})</div>}
    </div>
  );
}
